import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Users, UserPlus, Trash2, Crown, ShieldCheck, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { toast } from "sonner";

// Granular permissions, grouped for readability. Keys MUST match
// /app/backend/core/admin_perms.py → ALL_PERMS.
const PERM_GROUPS = [
  {
    label: ["Flotta", "Fleet"],
    items: [
      ["view_fleet",    ["Vede flotta",            "View fleet"]],
      ["manage_fleet",  ["Modifica veicoli e stato","Edit vehicles & status"]],
    ],
  },
  {
    label: ["Prenotazioni", "Bookings"],
    items: [
      ["view_bookings",   ["Vede prenotazioni online", "View online bookings"]],
      ["manage_bookings", ["Cancella / modifica prenotazioni", "Cancel / edit bookings"]],
    ],
  },
  {
    label: ["Contratti & Clienti", "Contracts & Customers"],
    items: [
      ["manage_contracts",  ["Crea contratti manuali e verbali", "Create manual contracts & forms"]],
      ["view_customers",    ["Vede anagrafica clienti",          "View customer dossiers"]],
      ["manage_customers",  ["Modifica / elimina clienti",        "Edit / delete customers"]],
    ],
  },
  {
    label: ["Marketing", "Marketing"],
    items: [
      ["manage_promos", ["Codici promo & convenzioni hotel", "Promo codes & hotel deals"]],
    ],
  },
  {
    label: ["Contabilità", "Accounting"],
    items: [
      ["view_revenue", ["Vede fatturato e KPI in euro", "View revenue & euro KPIs"]],
      ["export_excel", ["Export Excel per il commercialista", "Excel export for accountant"]],
    ],
  },
];

export default function AdminTeam() {
  const { i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [creating, setCreating] = useState(false);
  const emptyForm = { email: "", password: "", display_name: "", role_preset: "custom" };
  const [newForm, setNewForm] = useState(emptyForm);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/team");
      setTeam(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => {
    let active = true;
    api.get("/admin/team")
      .then((r) => { if (active) setTeam(r.data || []); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message));
    return () => { active = false; };
  }, []);

  const create = async () => {
    if (!newForm.email || !newForm.password || !newForm.display_name) {
      toast.error(isEN ? "Fill all required fields" : "Compila tutti i campi");
      return;
    }
    setCreating(true);
    try {
      // Sub-admins are created with role_preset="custom" → NO permissions.
      // The full admin will toggle them on per case in the list below.
      const body = newForm.role_preset === "full_admin"
        ? { ...newForm, role_preset: "full_admin" }
        : { ...newForm, role_preset: "custom", permissions: {} };
      await api.post("/admin/team", body);
      toast.success(isEN ? "Admin created" : "Admin creato");
      setNewForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const togglePerm = async (member, perm, value) => {
    try {
      await api.patch(`/admin/team/${member.id}`, { permissions: { [perm]: value } });
      // Optimistic update without full reload
      setTeam((prev) => prev.map((m) => m.id === member.id
        ? { ...m, permissions: { ...(m.permissions || {}), [perm]: value }, admin_role: "custom" }
        : m));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const promote = async (member) => {
    if (!window.confirm(isEN
      ? `Promote ${member.display_name} to Full Admin? They will be able to manage the team and create other admins.`
      : `Promuovere ${member.display_name} ad Admin Completo? Potrà gestire il team e creare altri admin.`)) return;
    try {
      await api.patch(`/admin/team/${member.id}`, { role_preset: "full_admin" });
      toast.success(isEN ? "Promoted to Full Admin" : "Promosso ad Admin Completo");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const demote = async (member) => {
    if (!window.confirm(isEN
      ? `Demote ${member.display_name} to Sub-Admin? All permissions will be cleared.`
      : `Declassare ${member.display_name} a Sub-Admin? Tutti i permessi verranno azzerati.`)) return;
    try {
      await api.patch(`/admin/team/${member.id}`, { role_preset: "custom", permissions: {} });
      toast.success(isEN ? "Demoted to Sub-Admin" : "Declassato a Sub-Admin");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggleActive = async (member) => {
    try {
      await api.patch(`/admin/team/${member.id}`, { active: !member.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (member) => {
    if (!window.confirm(isEN
      ? `Delete admin ${member.display_name}? This cannot be undone.`
      : `Eliminare l'admin ${member.display_name}? Operazione irreversibile.`)) return;
    try {
      await api.delete(`/admin/team/${member.id}`);
      toast.success(isEN ? "Admin deleted" : "Admin eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-team-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-1">Team</p>
        <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-2">
          <Users className="w-7 h-7 text-[#FFD700]" /> {isEN ? "Admins" : "Admin"}
        </h1>
        <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
          {isEN
            ? "Two tiers: Full Admins do everything (incl. managing the team). Sub-Admins see only what you grant below — granular toggles per person."
            : "Due livelli: gli Admin Completi possono fare tutto (incluso gestire il team). I Sub-Admin vedono solo ciò che attivi qui sotto — permessi granulari per persona."}
        </p>
      </div>

      {/* Create new */}
      <div className="border border-zinc-800 rounded-xl p-5 bg-zinc-900/30">
        <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#FFD700]" /> {isEN ? "Create new admin" : "Crea nuovo admin"}
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">{isEN ? "Display name" : "Nome"}</Label>
            <Input value={newForm.display_name}
                   onChange={(e) => setNewForm((f) => ({ ...f, display_name: e.target.value }))}
                   className="bg-zinc-950 border-zinc-800 mt-1" placeholder="Mario Rossi"
                   data-testid="team-new-name" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Email</Label>
            <Input type="email" value={newForm.email}
                   onChange={(e) => setNewForm((f) => ({ ...f, email: e.target.value }))}
                   className="bg-zinc-950 border-zinc-800 mt-1" placeholder="mario@rentlupielba.it"
                   data-testid="team-new-email" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">{isEN ? "Initial password (min 8)" : "Password iniziale (min 8)"}</Label>
            <Input type="text" value={newForm.password}
                   onChange={(e) => setNewForm((f) => ({ ...f, password: e.target.value }))}
                   className="bg-zinc-950 border-zinc-800 mt-1" placeholder="••••••••"
                   data-testid="team-new-password" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">{isEN ? "Tier" : "Livello"}</Label>
            <select value={newForm.role_preset}
                    onChange={(e) => setNewForm((f) => ({ ...f, role_preset: e.target.value }))}
                    className="block w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 mt-1 text-zinc-100 text-sm"
                    data-testid="team-new-role">
              <option value="custom">{isEN ? "Sub-Admin (no permissions yet)" : "Sub-Admin (nessun permesso ancora)"}</option>
              <option value="full_admin">{isEN ? "Full Admin (can do everything)" : "Admin Completo (può fare tutto)"}</option>
            </select>
            <p className="text-[11px] text-zinc-500 mt-1 leading-snug">
              {newForm.role_preset === "full_admin"
                ? (isEN ? "Same powers as you. They will be able to manage the team and create/delete other admins."
                        : "Stessi poteri tuoi. Potrà gestire il team e creare/eliminare altri admin.")
                : (isEN ? "Created with zero permissions. Toggle individual permissions in the list below after creation."
                        : "Creato senza permessi. Attiva poi i singoli permessi dalla lista qui sotto.")}
            </p>
          </div>
        </div>
        <Button onClick={create} disabled={creating}
                className="mt-4 bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold"
                data-testid="team-create-button">
          {creating ? "..." : (isEN ? "Create admin" : "Crea admin")}
        </Button>
      </div>

      {/* List */}
      <div className="space-y-4" data-testid="team-list">
        {team.map((m) => {
          const isFull = !!m.is_main_admin;
          const isSelf = m.id === user?.id;
          const perms = m.permissions || {};
          return (
            <div key={m.id}
                 className={`border rounded-xl p-5 ${isFull ? "border-[#FFD700]/40 bg-[#FFD700]/[0.04]" : "border-zinc-800 bg-zinc-900/30"}`}
                 data-testid="team-row">
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {isFull
                      ? <Crown className="w-5 h-5 text-[#FFD700]" />
                      : <ShieldCheck className="w-5 h-5 text-zinc-400" />}
                    <h3 className="font-display font-bold text-lg truncate">{m.display_name}</h3>
                    {isFull
                      ? <Badge className="bg-[#FFD700]/20 text-[#FFD700] border-0">{isEN ? "Full Admin" : "Admin Completo"}</Badge>
                      : <Badge className="bg-zinc-800 text-zinc-300 border-0">Sub-Admin</Badge>}
                    {isSelf && <Badge className="bg-blue-500/20 text-blue-300 border-0">{isEN ? "You" : "Tu"}</Badge>}
                    {!m.active && <Badge className="bg-red-500/20 text-red-300 border-0">{isEN ? "Disabled" : "Disattivato"}</Badge>}
                  </div>
                  <p className="text-sm text-zinc-500 mt-1 truncate">{m.email}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {!isFull && (
                    <Button size="sm" variant="ghost"
                            onClick={() => promote(m)}
                            className="text-xs text-[#FFD700] hover:bg-[#FFD700]/10"
                            data-testid={`team-promote-${m.id}`}>
                      <ArrowUpCircle className="w-4 h-4 mr-1" /> {isEN ? "Promote to Full" : "Promuovi a Completo"}
                    </Button>
                  )}
                  {isFull && !isSelf && (
                    <Button size="sm" variant="ghost"
                            onClick={() => demote(m)}
                            className="text-xs text-zinc-400 hover:text-orange-300 hover:bg-orange-500/10"
                            data-testid={`team-demote-${m.id}`}>
                      <ArrowDownCircle className="w-4 h-4 mr-1" /> {isEN ? "Demote to Sub" : "Declassa a Sub"}
                    </Button>
                  )}
                  {!isSelf && (
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(m)}
                            className="text-xs text-zinc-400 hover:text-[#FFD700]">
                      {m.active ? (isEN ? "Disable" : "Disattiva") : (isEN ? "Enable" : "Riattiva")}
                    </Button>
                  )}
                  {!isSelf && (
                    <Button size="sm" variant="ghost" onClick={() => remove(m)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`team-delete-${m.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Per-row permission grid — hidden for Full Admins (they bypass all) */}
              {!isFull && (
                <div className="mt-2 border-t border-zinc-800 pt-4">
                  <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">
                    {isEN ? "Granular permissions" : "Permessi granulari"}
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
                    {PERM_GROUPS.map((g) => (
                      <div key={g.label[0]} className="space-y-2">
                        <p className="text-xs font-semibold text-[#FFD700]">{isEN ? g.label[1] : g.label[0]}</p>
                        {g.items.map(([key, labels]) => (
                          <label key={key}
                                 className="flex items-center justify-between gap-3 text-sm text-zinc-300 cursor-pointer"
                                 data-testid={`perm-row-${m.id}-${key}`}>
                            <span className="leading-tight">{isEN ? labels[1] : labels[0]}</span>
                            <Switch
                              checked={!!perms[key]}
                              onCheckedChange={(v) => togglePerm(m, key, v)}
                              data-testid={`perm-toggle-${m.id}-${key}`}
                            />
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
